import { Box, InputAdornment } from "@mui/material";
import { StyledEngineProvider } from "@mui/material/styles";
import { ApiRouteType } from "@verity/api-client/types";
import { ThemeWrapper } from "@verity/components";
import { GenericButton, SettingsCard, SettingsCardField } from "@verity/components/common";
import { CoreCopyButton, CoreSwitch, CoreTextField, CoreTextFieldProps, coreToast } from "@verity/components/core";
import { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import apiClient from "../../../../../helpers/api-client";
import { PortalControllerContext } from "../../../../context";
import AttributeMappingCardIcon from "../assets/attributeMappingCardIcon.svg?react";
import DiscardChangesIcon from "../assets/discardChangesIcon.svg?react";
import IdentityProviderCardIcon from "../assets/identityProviderCardIcon.svg?react";
import MainCardIcon from "../assets/mainCardIcon.svg?react";
import RoleMappingCardIcon from "../assets/roleMappingCardIcon.svg?react";
import LinkageCardIcon from "../assets/linkageCardIcon.svg?react";
import SaveChangesIcon from "../assets/saveChangesIcon.svg?react";
import styles from "./SamlIntegration.module.css";

type NotNull<T> = T extends null ? never : T;
type AssertingPartyDetails = NotNull<
	ApiRouteType<"saml", "getRelyingPartyRegistrations">["content"][number]["assertingPartyDetails"]
>;
type AttributeMapping = NotNull<
	ApiRouteType<"saml", "getRelyingPartyRegistrations">["content"][number]["attributeMapping"]
>;
type RoleMapping = {
	id: number;
	attribute: string;
	privilegeGroupId: number;
	requiredParentPrivilegeGroupId?: number | null;
};
type RelyingPartyRegistrationMetadata = {
	instituteId: string;
	registrationId: string | null;
	metadataLocation: string | null;
	nameIdFormat: string | null;
	linkUsersByEmail: boolean;
	enabled: boolean;
};

type LtiClientRegistration = ApiRouteType<"lti", "clientRegistration">;

const ROLE_MAPPING_PRESETS = ["ADMIN", "INSTRUCTOR", "STUDENT"] as const;
type AvailableRoles = (typeof ROLE_MAPPING_PRESETS)[number];

export const SamlIntegration = ({ instituteUuid }: { instituteUuid: string }) => {
	const { t } = useTranslation();
	const { portalContext } = useContext(PortalControllerContext);
	const { lms } = portalContext;

	const [assertingPartyDetails, setAssertingPartyDetails] = useState<AssertingPartyDetails>({
		entityId: null,
		wantAuthnRequestsSigned: null,
		singleSignOnServiceLocation: null,
		singleSignOnServiceBindingUrn: null,
		singleLogoutServiceLocation: null,
		singleLogoutServiceResponseLocation: null,
		singleLogoutServiceBindingUrn: null,
		signingAlgorithms: [],
		verificationX509Credentials: [],
		encryptionX509Credentials: [],
	});

	const [attributeMapping, setAttributeMapping] = useState<AttributeMapping>({
		email: null,
		role: null,
		name: null,
		linkage: null,
	});

	const [roleMappingPresets, setRoleMappingPresets] = useState<ApiRouteType<"saml", "getRoleMappingPresets">>([]);

	const [mappedRoles, setMappedRoles] = useState<Record<AvailableRoles, RoleMapping | null>>({
		ADMIN: null,
		INSTRUCTOR: null,
		STUDENT: null,
	});

	const [relyingPartyRegistrationMetadata, setRelyingPartyRegistrationMetadata] =
		useState<RelyingPartyRegistrationMetadata>({
			instituteId: instituteUuid,
			registrationId: null,
			metadataLocation: null,
			nameIdFormat: null,
			linkUsersByEmail: false,
			enabled: false,
		});

	const [ltiClientRegistration, setLtiClientRegistration] = useState<LtiClientRegistration | undefined>(undefined);

	const fetchAndSetRelyingPartyRegistration = useCallback(
		async ({
			instituteUuid,
			mappedRoles,
		}: {
			instituteUuid: string;
			mappedRoles: Record<AvailableRoles, RoleMapping | null>;
		}) => {
			const response = await apiClient.saml.getRelyingPartyRegistrations({
				instituteId: instituteUuid,
				enabled: undefined,
			});
			if (response.content.length > 0) {
				const relyingPartyRegistration = response.content[0];

				const roleMappingPresets = await apiClient.saml.getRoleMappingPresets({
					registrationId: relyingPartyRegistration.registrationId,
				});
				setRoleMappingPresets(roleMappingPresets);
				const currentMappedRolesCopy = { ...mappedRoles };

				if (relyingPartyRegistration.roleMappings) {
					for (const roleMapping of relyingPartyRegistration.roleMappings) {
						for (const preset of roleMappingPresets) {
							if (
								roleMapping.privilegeGroupId === preset.privilegeGroupId &&
								ROLE_MAPPING_PRESETS.includes(preset.preset)
							) {
								currentMappedRolesCopy[preset.preset] = roleMapping;
							}
						}
					}
					setMappedRoles(currentMappedRolesCopy);
				}

				setRelyingPartyRegistrationMetadata({
					instituteId: instituteUuid,
					registrationId: relyingPartyRegistration.registrationId,
					metadataLocation: relyingPartyRegistration.metadataLocation,
					nameIdFormat: relyingPartyRegistration.nameIdFormat,
					linkUsersByEmail: relyingPartyRegistration.linkUsersByEmail,
					enabled: relyingPartyRegistration.enabled,
				});
				if (relyingPartyRegistration.assertingPartyDetails) {
					setAssertingPartyDetails(relyingPartyRegistration.assertingPartyDetails);
				}
				if (relyingPartyRegistration.attributeMapping) {
					setAttributeMapping(relyingPartyRegistration.attributeMapping);
				}
			}
		},
		[],
	);

	const fetchAndSetLtiClientRegistration = useCallback(async (instituteUuid: string) => {
		const response = await apiClient.lti.getInstituteClientRegistrations(instituteUuid);
		if (response.clientRegistrations.length > 0) {
			setLtiClientRegistration(response.clientRegistrations[0]);
		}
	}, []);

	const createOrUpdateRelyingPartyRegistration = useCallback(
		async ({
			relyingPartyRegistrationMetadata,
			assertingPartyDetails,
			attributeMapping,
			mappedRoles,
		}: {
			relyingPartyRegistrationMetadata: RelyingPartyRegistrationMetadata;
			assertingPartyDetails: AssertingPartyDetails;
			attributeMapping: AttributeMapping;
			mappedRoles: Record<AvailableRoles, RoleMapping | null>;
		}) => {
			let refreshedRelyingPartyRegistration:
				| ApiRouteType<"saml", "getRelyingPartyRegistrations">["content"][number]
				| null = null;
			if (relyingPartyRegistrationMetadata.registrationId === null) {
				refreshedRelyingPartyRegistration = await apiClient.saml.createRelyingPartyRegistration({
					createRequestBody: {
						...relyingPartyRegistrationMetadata,
						assertingPartyDetails,
						attributeMapping,
						roleMappings: [],
					},
				});
			} else {
				refreshedRelyingPartyRegistration = await apiClient.saml.updateRelyingPartyRegistration({
					registrationId: relyingPartyRegistrationMetadata.registrationId,
					updateRequestBody: {
						...relyingPartyRegistrationMetadata,
						assertingPartyDetails,
						attributeMapping,
					},
				});
			}

			for (const existingRoleMapping of refreshedRelyingPartyRegistration?.roleMappings ?? []) {
				await apiClient.saml.deleteRoleMapping({
					registrationId: refreshedRelyingPartyRegistration.registrationId,
					roleMappingId: existingRoleMapping.id,
				});
			}

			for (const roleMapping of Object.values(mappedRoles)) {
				if (
					roleMapping !== null &&
					roleMapping.attribute !== null &&
					roleMapping.attribute.length > 0 &&
					roleMapping.privilegeGroupId !== null
				) {
					await apiClient.saml.addRoleMapping({
						registrationId: refreshedRelyingPartyRegistration.registrationId,
						attribute: roleMapping.attribute,
						privilegeGroupId: roleMapping.privilegeGroupId,
						requiredParentPrivilegeGroupId: roleMapping.requiredParentPrivilegeGroupId ?? undefined,
					});
				}
			}
		},
		[],
	);

	useEffect(() => {
		void fetchAndSetRelyingPartyRegistration({ instituteUuid, mappedRoles });
		void fetchAndSetLtiClientRegistration(instituteUuid);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<StyledEngineProvider injectFirst>
			<ThemeWrapper scoped={false}>
				<Box className={styles.integrationContainer}>
					<Box className={styles.actions}>
						<GenericButton
							custom={{
								kind: "delete",
							}}
							hooks={{
								onClick: async () => {
									try {
										await fetchAndSetRelyingPartyRegistration({ instituteUuid, mappedRoles });
										coreToast.success(t("portal.components.integrationSettings.saml.toast.discard"));
									} catch (_) {
										coreToast.error(t("portal.components.integrationSettings.saml.toast.error"));
									}
								},
							}}
							renderers={{
								iconRenderer: () => <DiscardChangesIcon />,
								textRenderer: () => "Discard",
							}}
						/>
						<GenericButton
							custom={{
								kind: "primary",
							}}
							hooks={{
								onClick: async () => {
									try {
										await createOrUpdateRelyingPartyRegistration({
											relyingPartyRegistrationMetadata,
											assertingPartyDetails,
											attributeMapping,
											mappedRoles,
										});
										if (ltiClientRegistration) {
											await apiClient.lti.updateClientRegistration({
												id: ltiClientRegistration.id,
												linkageAttribute: ltiClientRegistration.linkageAttribute,
												linkUsersByEmail: ltiClientRegistration.linkUsersByEmail,
											});
										}
										await fetchAndSetRelyingPartyRegistration({ instituteUuid, mappedRoles });
										await fetchAndSetLtiClientRegistration(instituteUuid);
										coreToast.success(t("portal.components.integrationSettings.saml.toast.success"));
									} catch (_) {
										coreToast.error(t("portal.components.integrationSettings.saml.toast.error"));
									}
								},
							}}
							renderers={{
								iconRenderer: () => <SaveChangesIcon />,
								textRenderer: () => "Save",
							}}
						/>
					</Box>
					<Box className={styles.cardGrid}>
						<div className={styles.cardColumn}>
							<SamlIntegrationMainCard
								relyingPartyRegistrationMetadata={relyingPartyRegistrationMetadata}
								setRelyingPartyRegistrationMetadata={setRelyingPartyRegistrationMetadata}
							/>
							<SamlIntegrationIdentityProviderCard
								assertingPartyDetails={assertingPartyDetails}
								setAssertingPartyDetails={setAssertingPartyDetails}
							/>
							{relyingPartyRegistrationMetadata.registrationId && (
								<SamlIntegrationServiceProviderCard registrationId={relyingPartyRegistrationMetadata.registrationId} />
							)}
						</div>
						<div className={styles.cardColumn}>
							<SamlIntegrationAttributeMappingCard
								attributeMapping={attributeMapping}
								setAttributeMapping={setAttributeMapping}
							/>
							{roleMappingPresets.length > 0 && (
								<SamlIntegrationRoleMappingCard
									mappedRoles={mappedRoles}
									setMappedRoles={setMappedRoles}
									roleMappingPresets={roleMappingPresets}
								/>
							)}
							{lms && ltiClientRegistration && (
								<SamlIntegrationLtiCrossLinkingCard
									samlLinkageAttribute={attributeMapping?.linkage ?? null}
									setSamlLinkageAttribute={(newAttribute) =>
										setAttributeMapping((prevAttributeMapping) => ({
											...prevAttributeMapping,
											linkage: newAttribute,
										}))
									}
									ltiLinkageAttribute={ltiClientRegistration.linkageAttribute ?? null}
									setLtiLinkageAttribute={(newAttribute) =>
										setLtiClientRegistration((prevClientRegistration) =>
											prevClientRegistration
												? {
														...prevClientRegistration,
														linkageAttribute: newAttribute,
													}
												: undefined,
										)
									}
									linkUsersByEmail={
										relyingPartyRegistrationMetadata.linkUsersByEmail && ltiClientRegistration.linkUsersByEmail
									}
									setLinkUsersByEmail={(value) => {
										setRelyingPartyRegistrationMetadata((prevRelyingPartyRegistrationMetadata) => ({
											...prevRelyingPartyRegistrationMetadata,
											linkUsersByEmail: value,
										}));
										setLtiClientRegistration((prevClientRegistration) =>
											prevClientRegistration
												? {
														...prevClientRegistration,
														linkUsersByEmail: value,
													}
												: undefined,
										);
									}}
								/>
							)}
						</div>
					</Box>
				</Box>
			</ThemeWrapper>
		</StyledEngineProvider>
	);
};

const SamlIntegrationMainCard = ({
	relyingPartyRegistrationMetadata,
	setRelyingPartyRegistrationMetadata,
}: {
	relyingPartyRegistrationMetadata: RelyingPartyRegistrationMetadata;
	setRelyingPartyRegistrationMetadata: (relyingPartyRegistrationMetadata: RelyingPartyRegistrationMetadata) => void;
}) => {
	const { t } = useTranslation();

	return (
		<SettingsCard
			renderers={{
				header: {
					iconRenderer: () => <MainCardIcon />,
					titleRenderer: () => t("portal.components.integrationSettings.saml.main.header"),
				},
			}}
		>
			<Box className={styles.settingsCardFields}>
				<SettingsCardField
					renderers={{
						label: {
							titleRenderer: () =>
								t("portal.components.integrationSettings.saml.main.fields.allowAuthenticationViaSSO.title"),
							subTitleRenderer: () =>
								t("portal.components.integrationSettings.saml.main.fields.allowAuthenticationViaSSO.subTitle"),
						},
					}}
					custom={{ orientation: "horizontal" }}
				>
					{(titleId) => (
						<Box className={styles.toggleWrapper}>
							<CoreSwitch
								checked={relyingPartyRegistrationMetadata.enabled}
								labelTextType="left"
								onChange={(_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
									setRelyingPartyRegistrationMetadata({
										...relyingPartyRegistrationMetadata,
										enabled: checked,
									});
								}}
								inputProps={{ "aria-labelledby": titleId }}
							/>
						</Box>
					)}
				</SettingsCardField>

				<SettingsCardField
					renderers={{
						label: {
							titleRenderer: () => t("portal.components.integrationSettings.saml.main.fields.metadataLocation.title"),
							subTitleRenderer: () =>
								t("portal.components.integrationSettings.saml.main.fields.metadataLocation.subTitle"),
						},
					}}
				>
					{(titleId) => (
						<CoreTextField
							value={convertNullableToString(relyingPartyRegistrationMetadata.metadataLocation)}
							onChange={(e) => {
								setRelyingPartyRegistrationMetadata({
									...relyingPartyRegistrationMetadata,
									metadataLocation: convertStringToNullable(e.target.value),
								});
							}}
							inputProps={{
								className: styles.settingsCardTextField,
								"aria-labelledby": titleId,
							}}
						/>
					)}
				</SettingsCardField>

				<SettingsCardField
					renderers={{
						label: {
							titleRenderer: () => t("portal.components.integrationSettings.saml.main.fields.nameIdFormat.title"),
							subTitleRenderer: () => t("portal.components.integrationSettings.saml.main.fields.nameIdFormat.subTitle"),
						},
					}}
				>
					{(titleId) => (
						<CoreTextField
							value={convertNullableToString(relyingPartyRegistrationMetadata.nameIdFormat)}
							onChange={(e) => {
								setRelyingPartyRegistrationMetadata({
									...relyingPartyRegistrationMetadata,
									nameIdFormat: convertStringToNullable(e.target.value),
								});
							}}
							inputProps={{
								className: styles.settingsCardTextField,
								"aria-labelledby": titleId,
							}}
						/>
					)}
				</SettingsCardField>
			</Box>
		</SettingsCard>
	);
};

const SamlIntegrationIdentityProviderCard = ({
	assertingPartyDetails,
	setAssertingPartyDetails,
}: {
	assertingPartyDetails: AssertingPartyDetails;
	setAssertingPartyDetails: (assertingPartyDetails: AssertingPartyDetails) => void;
}) => {
	const { t } = useTranslation();
	return (
		<SettingsCard
			renderers={{
				header: {
					iconRenderer: () => <IdentityProviderCardIcon />,
					titleRenderer: () => t("portal.components.integrationSettings.saml.identityProvider.header"),
				},
			}}
		>
			<Box className={styles.settingsCardFields}>
				<SettingsCardField
					renderers={{
						label: {
							titleRenderer: () =>
								t("portal.components.integrationSettings.saml.identityProvider.fields.entityId.title"),
							subTitleRenderer: () =>
								t("portal.components.integrationSettings.saml.identityProvider.fields.entityId.subTitle"),
						},
					}}
				>
					{(titleId) => (
						<CoreTextField
							value={convertNullableToString(assertingPartyDetails.entityId)}
							onChange={(e) => {
								setAssertingPartyDetails({
									...assertingPartyDetails,
									entityId: convertStringToNullable(e.target.value),
								});
							}}
							inputProps={{
								className: styles.settingsCardTextField,
								"aria-labelledby": titleId,
							}}
						/>
					)}
				</SettingsCardField>
				<SettingsCardField
					renderers={{
						label: {
							titleRenderer: () =>
								t(
									"portal.components.integrationSettings.saml.identityProvider.fields.singleSignOnServiceLocation.title",
								),
							subTitleRenderer: () =>
								t(
									"portal.components.integrationSettings.saml.identityProvider.fields.singleSignOnServiceLocation.subTitle",
								),
						},
					}}
				>
					{(titleId) => (
						<CoreTextField
							value={convertNullableToString(assertingPartyDetails.singleSignOnServiceLocation)}
							onChange={(e) => {
								setAssertingPartyDetails({
									...assertingPartyDetails,
									singleSignOnServiceLocation: convertStringToNullable(e.target.value),
								});
							}}
							inputProps={{
								className: styles.settingsCardTextField,
								"aria-labelledby": titleId,
							}}
						/>
					)}
				</SettingsCardField>

				<SettingsCardField
					renderers={{
						label: {
							titleRenderer: () =>
								t(
									"portal.components.integrationSettings.saml.identityProvider.fields.signingCertificateFingerprint.title",
								),
							subTitleRenderer: () =>
								t(
									"portal.components.integrationSettings.saml.identityProvider.fields.signingCertificateFingerprint.subTitle",
								),
						},
					}}
				>
					{(titleId) => (
						<CoreTextField
							value={
								assertingPartyDetails.verificationX509Credentials.length > 0
									? assertingPartyDetails.verificationX509Credentials[0].certificate
									: ""
							}
							onChange={(e) => {
								setAssertingPartyDetails({
									...assertingPartyDetails,
									verificationX509Credentials: [
										{
											type: "VERIFICATION",
											certificate: e.target.value,
										},
									],
								});
							}}
							inputProps={{
								className: styles.settingsCardTextField,
								"aria-labelledby": titleId,
							}}
						/>
					)}
				</SettingsCardField>
			</Box>
		</SettingsCard>
	);
};

function CopyOnlyTextField({
	value,
	copyButtonAriaLabel,
	...props
}: Pick<CoreTextFieldProps, "id" | "value" | "aria-labelledby"> & {
	copyButtonAriaLabel?: string;
}) {
	return (
		<CoreTextField
			{...props}
			value={value}
			size="large"
			autoComplete="off"
			fullWidth
			InputProps={{
				readOnly: true,
				endAdornment: (
					<InputAdornment position="end" style={{ paddingRight: "6px" }}>
						<CoreCopyButton text={value?.toString() ?? ""} aria-label={copyButtonAriaLabel} />
					</InputAdornment>
				),
			}}
			inputProps={{
				className: styles.settingsCardTextField,
			}}
		/>
	);
}

const SamlIntegrationServiceProviderCard = ({ registrationId }: { registrationId: string }) => {
	const { t } = useTranslation();
	const { portalContext } = useContext(PortalControllerContext);
	const { homeOrigin } = portalContext;
	return (
		<SettingsCard
			renderers={{
				header: {
					iconRenderer: () => <IdentityProviderCardIcon />,
					titleRenderer: () => t("portal.components.integrationSettings.saml.serviceProvider.header"),
				},
			}}
		>
			<Box className={styles.settingsCardFields}>
				<SettingsCardField
					renderers={{
						label: {
							titleRenderer: () => t("portal.components.integrationSettings.saml.serviceProvider.fields.issuer.title"),
							subTitleRenderer: () =>
								t("portal.components.integrationSettings.saml.serviceProvider.fields.issuer.subTitle"),
						},
					}}
				>
					{(titleId) => (
						<CopyOnlyTextField
							value={convertNullableToString(homeOrigin)}
							aria-labelledby={titleId}
							copyButtonAriaLabel={t("portal.components.integrationSettings.saml.serviceProvider.fields.issuer.copy")}
						/>
					)}
				</SettingsCardField>
				<SettingsCardField
					renderers={{
						label: {
							titleRenderer: () =>
								t("portal.components.integrationSettings.saml.serviceProvider.fields.assertionConsumerUrl.title"),
							subTitleRenderer: () =>
								t("portal.components.integrationSettings.saml.serviceProvider.fields.assertionConsumerUrl.subTitle"),
						},
					}}
				>
					{(titleId) => (
						<CopyOnlyTextField
							value={`${homeOrigin}/api/access-control/saml2/service-provider-metadata/${registrationId}`}
							aria-labelledby={titleId}
							copyButtonAriaLabel={t(
								"portal.components.integrationSettings.saml.serviceProvider.fields.assertionConsumerUrl.copy",
							)}
						/>
					)}
				</SettingsCardField>
			</Box>
		</SettingsCard>
	);
};

const SamlIntegrationAttributeMappingCard = ({
	attributeMapping,
	setAttributeMapping,
}: {
	attributeMapping: AttributeMapping;
	setAttributeMapping: (attributeMapping: AttributeMapping) => void;
}) => {
	const { t } = useTranslation();
	return (
		<SettingsCard
			renderers={{
				header: {
					iconRenderer: () => <AttributeMappingCardIcon />,
					titleRenderer: () => t("portal.components.integrationSettings.saml.attributeMapping.header"),
				},
			}}
		>
			<Box className={styles.settingsCardFields}>
				<SettingsCardField
					renderers={{
						label: {
							titleRenderer: () => t("portal.components.integrationSettings.saml.attributeMapping.fields.name.title"),
							subTitleRenderer: () =>
								t("portal.components.integrationSettings.saml.attributeMapping.fields.name.subTitle"),
						},
					}}
				>
					{(titleId) => (
						<CoreTextField
							value={convertNullableToString(attributeMapping?.name)}
							onChange={(e) => {
								setAttributeMapping({
									...attributeMapping,
									name: convertStringToNullable(e.target.value),
								});
							}}
							inputProps={{
								className: styles.settingsCardTextField,
								"aria-labelledby": titleId,
							}}
						/>
					)}
				</SettingsCardField>

				<SettingsCardField
					renderers={{
						label: {
							titleRenderer: () => t("portal.components.integrationSettings.saml.attributeMapping.fields.email.title"),
							subTitleRenderer: () =>
								t("portal.components.integrationSettings.saml.attributeMapping.fields.email.subTitle"),
						},
					}}
				>
					{(titleId) => (
						<CoreTextField
							value={convertNullableToString(attributeMapping?.email)}
							onChange={(e) => {
								setAttributeMapping({
									...attributeMapping,
									email: convertStringToNullable(e.target.value),
								});
							}}
							inputProps={{
								className: styles.settingsCardTextField,
								"aria-labelledby": titleId,
							}}
						/>
					)}
				</SettingsCardField>

				<SettingsCardField
					renderers={{
						label: {
							titleRenderer: () => t("portal.components.integrationSettings.saml.attributeMapping.fields.role.title"),
							subTitleRenderer: () =>
								t("portal.components.integrationSettings.saml.attributeMapping.fields.role.subTitle"),
						},
					}}
				>
					{(titleId) => (
						<CoreTextField
							value={convertNullableToString(attributeMapping?.role)}
							onChange={(e) => {
								setAttributeMapping({
									...attributeMapping,
									role: convertStringToNullable(e.target.value),
								});
							}}
							inputProps={{
								className: styles.settingsCardTextField,
								"aria-labelledby": titleId,
							}}
						/>
					)}
				</SettingsCardField>
			</Box>
		</SettingsCard>
	);
};

const SamlIntegrationRoleMappingCard = ({
	mappedRoles,
	setMappedRoles,
	roleMappingPresets,
}: {
	mappedRoles: Record<AvailableRoles, RoleMapping | null>;
	setMappedRoles: (mappedRoles: Record<AvailableRoles, RoleMapping | null>) => void;
	roleMappingPresets: ApiRouteType<"saml", "getRoleMappingPresets">;
}) => {
	const { t } = useTranslation();
	const { portalContext } = useContext(PortalControllerContext);

	const visiblePresets = portalContext.lms
		? ROLE_MAPPING_PRESETS
		: ROLE_MAPPING_PRESETS.filter((preset) => preset !== "STUDENT");

	return (
		<SettingsCard
			renderers={{
				header: {
					iconRenderer: () => <RoleMappingCardIcon />,
					titleRenderer: () => t("portal.components.integrationSettings.saml.roleMapping.header"),
				},
			}}
		>
			<Box className={styles.settingsCardFields}>
				{visiblePresets.map((preset, index) => {
					const roleMapping = mappedRoles[preset];
					return (
						<SettingsCardField
							key={index}
							renderers={{
								label: {
									titleRenderer: () =>
										t(`portal.components.integrationSettings.saml.roleMapping.fields.${preset}.title`),
									subTitleRenderer: () =>
										t(`portal.components.integrationSettings.saml.roleMapping.fields.${preset}.subTitle`),
								},
							}}
						>
							{(titleId) => (
								<CoreTextField
									value={roleMapping ? roleMapping.attribute : ""}
									onChange={(e) => {
										const currentMappedRolesCopy = { ...mappedRoles };
										const roleMappingPreset = roleMappingPresets.find(
											(roleMappingPreset) => roleMappingPreset.preset === preset,
										);
										if (!roleMappingPreset) {
											return;
										}

										if (e.target.value.length === 0) {
											currentMappedRolesCopy[preset] = null;
										} else {
											currentMappedRolesCopy[preset] = {
												attribute: e.target.value,
												privilegeGroupId: roleMappingPreset.privilegeGroupId,
												requiredParentPrivilegeGroupId: roleMappingPreset.requiredParentPrivilegeGroupId ?? null,
												id: 0,
											};
										}
										setMappedRoles(currentMappedRolesCopy);
									}}
									inputProps={{
										className: styles.settingsCardTextField,
										"aria-labelledby": titleId,
									}}
								/>
							)}
						</SettingsCardField>
					);
				})}
			</Box>
		</SettingsCard>
	);
};

const SamlIntegrationLtiCrossLinkingCard = ({
	samlLinkageAttribute,
	setSamlLinkageAttribute,
	ltiLinkageAttribute,
	setLtiLinkageAttribute,
	linkUsersByEmail,
	setLinkUsersByEmail,
}: {
	samlLinkageAttribute: string | null;
	setSamlLinkageAttribute: (newAttribute: string | null) => void;
	ltiLinkageAttribute: string | null;
	setLtiLinkageAttribute: (newAttribute: string | null) => void;
	linkUsersByEmail: boolean;
	setLinkUsersByEmail: (value: boolean) => void;
}) => {
	const { t } = useTranslation();

	return (
		<SettingsCard
			renderers={{
				header: {
					iconRenderer: () => <LinkageCardIcon />,
					titleRenderer: () => t("portal.components.integrationSettings.saml.ltiCrossLinking.header"),
				},
			}}
		>
			<Box className={styles.settingsCardFields}>
				<SettingsCardField
					renderers={{
						label: {
							titleRenderer: () =>
								t("portal.components.integrationSettings.saml.ltiCrossLinking.fields.samlAttribute.title"),
							subTitleRenderer: () =>
								t("portal.components.integrationSettings.saml.ltiCrossLinking.fields.samlAttribute.subTitle"),
						},
					}}
				>
					{(titleId) => (
						<CoreTextField
							value={convertNullableToString(samlLinkageAttribute)}
							onChange={(e) => {
								setSamlLinkageAttribute(convertStringToNullable(e.target.value));
							}}
							inputProps={{
								className: styles.settingsCardTextField,
								"aria-labelledby": titleId,
							}}
						/>
					)}
				</SettingsCardField>
				<SettingsCardField
					renderers={{
						label: {
							titleRenderer: () =>
								t("portal.components.integrationSettings.saml.ltiCrossLinking.fields.ltiAttribute.title"),
							subTitleRenderer: () =>
								t("portal.components.integrationSettings.saml.ltiCrossLinking.fields.ltiAttribute.subTitle"),
						},
					}}
				>
					{(titleId) => (
						<CoreTextField
							value={convertNullableToString(ltiLinkageAttribute)}
							onChange={(e) => {
								setLtiLinkageAttribute(convertStringToNullable(e.target.value));
							}}
							inputProps={{
								className: styles.settingsCardTextField,
								"aria-labelledby": titleId,
							}}
						/>
					)}
				</SettingsCardField>
				<SettingsCardField
					renderers={{
						label: {
							titleRenderer: () =>
								t("portal.components.integrationSettings.saml.ltiCrossLinking.fields.emailFallback.title"),
							subTitleRenderer: () =>
								t("portal.components.integrationSettings.saml.ltiCrossLinking.fields.emailFallback.subTitle"),
						},
					}}
					custom={{ orientation: "horizontal" }}
				>
					{(titleId) => (
						<Box className={styles.toggleWrapper}>
							<CoreSwitch
								checked={linkUsersByEmail}
								labelTextType="left"
								onChange={(_event: unknown, checked: boolean) => setLinkUsersByEmail(checked)}
								inputProps={{ "aria-labelledby": titleId }}
							/>
						</Box>
					)}
				</SettingsCardField>
			</Box>
		</SettingsCard>
	);
};

const convertStringToNullable = (value: string) => (value.length === 0 ? null : value);
const convertNullableToString = (value: string | null) => value ?? "";
